/**
 * Settings -> Integrations: the CRM's OWN Stripe account.
 *
 * NOT the Husly platform's Stripe keys, and the panel says so in its own
 * description rather than leaving it to be inferred. Two accounts, two sets of
 * keys, two webhook endpoints, and the failure mode of confusing them is that
 * client card payments land in the company's SaaS revenue account. That sentence
 * is the reason this card exists as its own tab instead of a second section on
 * the platform's billing screen.
 *
 * WHAT THE OPERATOR IS ACTUALLY CONFIGURING. Stripe here is a payment rail, not
 * a billing system: the CRM issues the invoice, with our number on it, and this
 * lets a client settle it by card instead of by transfer. There is no product,
 * no price and no subscription to set up on the Stripe side.
 *
 * The two fields that are not obvious:
 *   - the WEBHOOK SECRET is what makes the money arrive in our books. Without
 *     it Stripe can take the payment and we never hear about it, so the invoice
 *     sits unpaid while the client's card is charged. The panel says that out
 *     loud rather than treating it as an optional extra.
 *   - `CRM_VAULT_KEY` has to exist before a key can be stored at all, because
 *     these secrets are encrypted at rest. The panel refuses up front instead of
 *     letting somebody paste a live key into a form that then fails.
 */
import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, CreditCard, PlugZap, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getCrmBillingSettings,
  saveCrmBillingSettings,
  testCrmStripe,
  type CrmBillingSettings,
  type CrmStripeMode,
  type CrmStripeTestResult,
} from "../../../../lib/crmBilling";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, Note, SaveRow, SecretPill, SelectPill, ToggleRow } from "../panel/fields";

export function CrmStripeCard() {
  const t = useT();
  const [settings, setSettings] = useState<CrmBillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<CrmStripeMode>("test");
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error" | "vault">("idle");
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<CrmStripeTestResult | null>(null);

  function hydrate(s: CrmBillingSettings) {
    setSettings(s);
    setEnabled(s.enabled);
    setMode(s.mode);
  }

  useEffect(() => {
    getCrmBillingSettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      // The typed secrets go under the mode they were typed for, and only when
      // something was typed. An empty field means "keep what is stored" all the
      // way down to the server - the form never receives a secret, so it cannot
      // send one back, and treating empty as a deletion would wipe the key every
      // time somebody flipped the switch.
      await saveCrmBillingSettings({
        enabled,
        mode,
        [mode]: {
          ...(secretKey.trim() ? { secretKey: secretKey.trim() } : {}),
          ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
        },
      });
      const updated = await getCrmBillingSettings();
      hydrate(updated);
      setSecretKey("");
      setWebhookSecret("");
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch (err) {
      // The one refusal worth naming: there is no encryption key, so the secret
      // was not stored. "Something went wrong" would have somebody paste the key
      // again and watch it fail again.
      const code = (err as { body?: { error?: string } })?.body?.error;
      setSaveState(code === "vault_key_missing" ? "vault" : "error");
    }
  }

  async function handleTest() {
    setTesting(true);
    setTest(null);
    try {
      setTest(await testCrmStripe());
    } catch (err) {
      const body = (err as { body?: CrmStripeTestResult })?.body;
      setTest(body && typeof body.ok === "boolean" ? body : { ok: false, error: "network" });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <PanelLoader />;

  const here = mode === "live" ? settings?.live : settings?.test;
  const isConfigured = Boolean(settings?.enabled);
  // The mode switch and the key's own prefix disagreeing is the quiet
  // misconfiguration: everything looks set up and the charges go to the wrong
  // environment, or fail with an error that names neither.
  const modeMismatch = Boolean(
    settings?.activeKeyMode && settings.activeKeyMode !== settings.mode,
  );

  return (
    <IntegrationCard
      icon={CreditCard}
      title={t("settings.crmStripe.title")}
      desc={t("settings.crmStripe.desc")}
      status={
        <ConnStatus
          ok={isConfigured}
          okLabel={t("settings.crmStripe.statusConfigured")}
          offLabel={t("settings.crmStripe.statusNotConfigured")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        <Note tone="warn">{t("settings.crmStripe.notPlatformAccount")}</Note>

        {settings && !settings.vaultReady ? (
          <Note tone="bad">
            {settings.vaultReason === "invalid"
              ? t("settings.crmStripe.vaultInvalid")
              : t("settings.crmStripe.vaultMissing")}
          </Note>
        ) : null}

        {settings?.source === "env" ? <Note tone="warn">{t("settings.crmStripe.fromEnv")}</Note> : null}

        {modeMismatch ? (
          <Note tone="bad">
            {t("settings.crmStripe.modeMismatch", { mode: t(`settings.crmStripe.mode.${settings?.activeKeyMode}`) })}
          </Note>
        ) : null}

        <ToggleRow
          label={t("settings.crmStripe.enabled")}
          hint={t("settings.crmStripe.enabledHint")}
          checked={enabled}
          onChange={setEnabled}
        />

        <Field label={t("settings.crmStripe.modeLabel")} hint={t("settings.crmStripe.modeHint")}>
          <SelectPill value={mode} onChange={(v) => setMode(v as CrmStripeMode)}>
            <option value="test">{t("settings.crmStripe.mode.test")}</option>
            <option value="live">{t("settings.crmStripe.mode.live")}</option>
          </SelectPill>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("settings.crmStripe.secretKey")}
            hint={here?.secretKeySet && !secretKey ? t("settings.crmStripe.kept") : undefined}
          >
            <SecretPill
              value={secretKey}
              onChange={setSecretKey}
              placeholder={here?.secretKeySet ? t("settings.crmStripe.stored") : "sk_test_..."}
              reveal
              showLabel={t("settings.crmStripe.showPass")}
              hideLabel={t("settings.crmStripe.hidePass")}
            />
          </Field>

          <Field
            label={t("settings.crmStripe.webhookSecret")}
            hint={here?.webhookSecretSet && !webhookSecret ? t("settings.crmStripe.kept") : undefined}
          >
            <SecretPill
              value={webhookSecret}
              onChange={setWebhookSecret}
              placeholder={here?.webhookSecretSet ? t("settings.crmStripe.stored") : "whsec_..."}
              reveal
              showLabel={t("settings.crmStripe.showPass")}
              hideLabel={t("settings.crmStripe.hidePass")}
            />
          </Field>
        </div>

        {/* The endpoint URL is not a setting - it is what has to be pasted into
            Stripe's own dashboard, and getting it wrong is the commonest reason
            a payment is taken and never lands in the books. */}
        <Note tone="mut">
          {t("settings.crmStripe.webhookHint")}
          <code className="ml-1 select-all rounded bg-black/5 px-1.5 py-0.5 text-[11.5px]">
            {window.location.origin}/api/crm/stripe/webhook
          </code>
        </Note>

        {test ? <TestReport t={t} test={test} /> : null}

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saveState === "saving" ? t("settings.crmStripe.saving") : t("settings.crmStripe.save")}
          </Btn>
          <Btn onClick={handleTest} disabled={testing}>
            {testing ? <RefreshCw className="size-3.5 animate-spin" /> : <PlugZap className="size-3.5" />}
            {testing ? t("settings.crmStripe.testing") : t("settings.crmStripe.test")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.crmStripe.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.crmStripe.saveError")}</StatusPill> : null}
          {saveState === "vault" ? <StatusPill tone="lt">{t("settings.crmStripe.vaultMissing")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}

/**
 * What Stripe said when asked to identify itself.
 *
 * The account NAME is the point, not the tick. A key that authenticates proves
 * only that some account answered; seeing the account's own name is how an
 * operator confirms it is the CRM's account and not the platform's - which is
 * the exact mistake this whole panel is arranged around preventing.
 *
 * `chargesEnabled` is reported separately because a Stripe account can be real,
 * reachable and still not allowed to take money, and that failure would
 * otherwise only surface at a client's card form.
 */
function TestReport({ t, test }: { t: ReturnType<typeof useT>; test: CrmStripeTestResult }) {
  if (!test.ok) {
    return (
      <Note tone="bad" icon={<AlertCircle className="mt-0.5 size-4 shrink-0" />} live>
        <span className="block font-[550]">{t("settings.crmStripe.testFail")}</span>
        {test.message ? <span className="mt-0.5 block text-[12px] opacity-80">{test.message}</span> : null}
      </Note>
    );
  }
  return (
    <Note tone="ok" icon={<CheckCircle2 className="mt-0.5 size-4 shrink-0" />} live>
      <span className="block font-[550]">
        {t("settings.crmStripe.testOk", { name: test.name || test.accountId || "-" })}
      </span>
      {test.chargesEnabled === false ? (
        <span className="mt-0.5 block text-[12px] opacity-80">{t("settings.crmStripe.testNoCharges")}</span>
      ) : null}
    </Note>
  );
}
